import { useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { quanLyModuleRepository } from '../../../lib/repositories/quan-ly-module.repository';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { gomTheoCode } from '../nghiep-vu/thao-tac';
import type { ModulePackageResult, ModuleVersion } from '../types';

const KHOA = ['quan-ly-module'] as const;

/** Lỗi 4xx là câu trả lời của server, không phải trục trặc — không thử lại. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** `GET /api/v1/platform/modules`, gom theo `code` phía client — dữ liệu dùng chung cho cả danh sách và chi tiết. */
export function useDanhSachPhienBan() {
  return useQuery<ModuleVersion[]>({
    queryKey: [...KHOA, 'phien-ban'],
    queryFn: quanLyModuleRepository.danhSachPhienBan,
    staleTime: 30_000,
    retry: thuLai,
  });
}

/** Danh sách module cho màn danh sách — mỗi dòng một `code`, gồm toàn bộ lịch sử phiên bản của nó. */
export function useDanhSachModule() {
  const { data, ...rest } = useDanhSachPhienBan();
  const items = useMemo(() => (data ? gomTheoCode(data) : undefined), [data]);
  return { ...rest, data: items };
}

/** Chi tiết một module theo `code` — cùng nguồn dữ liệu với danh sách, không gọi thêm request. */
export function useChiTietModule(code: string) {
  const { data, ...rest } = useDanhSachPhienBan();
  const module = useMemo(() => {
    if (!data) return undefined;
    const cua = data.filter((p) => p.code === code).sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
    if (cua.length === 0) return null;
    return { code, name: cua[0].name, vendor: cua[0].vendor, phienBan: cua };
  }, [data, code]);
  return { ...rest, data: module };
}

function useLamMoi() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: KHOA });
}

/**
 * Nộp gói. Lỗi **không** hiện toast: khu vực upload giữ nguyên tệp đã chọn và
 * hiện lỗi ngay tại chỗ (`LoiNopGoi`), để người nộp đọc được chi tiết vi phạm
 * thay vì một dòng toast thoáng qua.
 */
export function useNopGoiModule() {
  const lamMoi = useLamMoi();
  // Một khoá lập cho mỗi lần mở hộp thoại; đổi tệp vẫn giữ khoá cũ để nộp lại
  // cùng tệp không tạo hai bản ghi khi yêu cầu trước bị timeout ở phía client.
  const khoaLapRef = useRef<string>(crypto.randomUUID());
  return useMutation<ModulePackageResult, unknown, File>({
    mutationFn: (tep: File) => quanLyModuleRepository.nopGoi(tep, khoaLapRef.current),
    onSuccess: () => {
      lamMoi();
      khoaLapRef.current = crypto.randomUUID();
    },
  });
}

export function useDuyetModule() {
  const lamMoi = useLamMoi();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (bien: { code: string; version: string }) =>
      quanLyModuleRepository.duyet(bien.code, bien.version),
    onSuccess: (v: ModuleVersion) => {
      lamMoi();
      toast.success(t('quanLyModule.daDuyet', { phienBan: v.version }));
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}

export function useTuChoiModule() {
  const lamMoi = useLamMoi();
  const toast = useToast();
  const { t } = useT();
  return useMutation({
    mutationFn: (bien: { code: string; version: string; reason: string }) =>
      quanLyModuleRepository.tuChoi(bien.code, bien.version, bien.reason),
    onSuccess: (v: ModuleVersion) => {
      lamMoi();
      toast.success(t('quanLyModule.daTuChoi', { phienBan: v.version }));
    },
    // Lỗi không toast: hộp từ chối giữ nguyên lý do vừa nhập và hiện lỗi ngay trong hộp.
  });
}

export function useXoaModule() {
  const lamMoi = useLamMoi();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (bien: { code: string; ten: string }) => quanLyModuleRepository.xoa(bien.code),
    onSuccess: (_r, bien) => {
      lamMoi();
      toast.success(t('quanLyModule.daXoa', { ten: bien.ten }));
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}
